// CR051: Templates Tab - Browse and instantiate recipe templates
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FileStack, Copy, ArrowRight, Clock, Camera, ScrollIcon, Map } from 'lucide-react';
import type { RecipeTemplate } from '@/pages/admin/FlyerCaptureConsole';

interface TemplatesTabProps {
  templates: RecipeTemplate[];
  onCreateFromTemplate: (template: RecipeTemplate) => void;
}

const VIEWER_ICONS: Record<string, typeof FileStack> = {
  carousel: Camera,
  html_scroll: ScrollIcon,
  flipp: Map,
  embedded_pdf: FileStack,
};

export const TemplatesTab = ({ templates, onCreateFromTemplate }: TemplatesTabProps) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Recipe Templates</h2>
        <p className="text-sm text-muted-foreground">
          Start with a template and customize for your merchant
        </p>
      </div>

      <ScrollArea className="h-[600px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map((template) => {
            const ViewerIcon = VIEWER_ICONS[template.viewer_type || ''] || FileStack;
            const defaults = template.recipe_defaults as Record<string, unknown>;
            const stepCount = (template.steps as unknown[]).length;

            return (
              <Card key={template.id} className="relative">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <ViewerIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{template.recipe_name}</CardTitle>
                        <CardDescription className="text-xs">
                          {template.viewer_type || 'Generic'} viewer
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary">System</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {template.description || 'No description'}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">
                      {stepCount} steps
                    </Badge>
                    {defaults.max_pages && (
                      <Badge variant="outline" className="text-xs">
                        Max {defaults.max_pages as number} pages
                      </Badge>
                    )}
                    {defaults.stitch_enabled && (
                      <Badge variant="outline" className="text-xs">
                        PDF Stitching
                      </Badge>
                    )}
                    {defaults.location_gate_enabled && (
                      <Badge variant="outline" className="text-xs">
                        Location Gate
                      </Badge>
                    )}
                  </div>

                  <div className="p-3 bg-muted rounded-lg">
                    <div className="text-xs text-muted-foreground mb-2">Key Settings</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Wait:</span>{' '}
                        {String(defaults.initial_wait_ms || 1200)}ms
                      </div>
                      <div>
                        <span className="text-muted-foreground">Min Pages:</span>{' '}
                        {String(defaults.min_pages_success || 6)}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Format:</span>{' '}
                        {String(defaults.image_format || 'jpg')}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Quality:</span>{' '}
                        {String(defaults.jpg_quality || 85)}%
                      </div>
                    </div>
                  </div>

                  <Button 
                    className="w-full" 
                    variant="outline"
                    onClick={() => onCreateFromTemplate(template)}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Create Recipe from Template
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}

          {templates.length === 0 && (
            <div className="col-span-2 text-center py-12 text-muted-foreground">
              No templates available
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
